import React, { useState, useRef, useEffect, useCallback } from 'react';
import styles from './Cards.module.css';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

const Cards = ({ 
  cards, 
  maxCardTitleHeight = 'auto',
  maxCardHeight = 'auto',
  maxCardsWidth = '1200px',
  maxCardWidth = 'calc(25% - 20px)', // По умолчанию 4 карточки в ряд
  carousel = false, // Параметр для включения карусели
  itemsPerSlide = 3, // Количество карточек в одном слайде карусели
  ...props 
}) => {
  const { t } = useTranslation();
  const carouselRef = useRef(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [slideWidth, setSlideWidth] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [dragStartTime, setDragStartTime] = useState(0);
  
  // Создаем массив с утроенным количеством элементов для плавной бесконечной прокрутки
  const repeatedCards = [...cards, ...cards, ...cards];
  
  // Вычисляем ширину элемента карусели при первом рендере и при изменении размеров
  useEffect(() => {
    if (!carousel || !carouselRef.current) return;
    
    const updateDimensions = () => {
      if (carouselRef.current) {
        // Вычисляем ширину одной карточки со всеми отступами
        const cardElement = carouselRef.current.querySelector(`.${styles.card}`);
        if (cardElement) {
          const cardStyles = window.getComputedStyle(cardElement);
          const cardWidth = cardElement.offsetWidth;
          const marginLeft = parseInt(cardStyles.marginLeft, 10) || 0;
          const marginRight = parseInt(cardStyles.marginRight, 10) || 0;
          
          // Учитываем gap между элементами, если он есть
          const gap = 20; // Соответствует значению gap в CSS
          
          setSlideWidth(cardWidth + marginLeft + marginRight + gap);
        }
      }
    };
    
    // Инициализация карусели
    updateDimensions();
    
    // Устанавливаем начальное положение карусели (начинаем с копии оригинальных карт)
    setTimeout(() => {
      if (carouselRef.current) {
        carouselRef.current.style.transition = 'none';
        carouselRef.current.style.transform = `translateX(-${cards.length * slideWidth}px)`;
        
        // Форсируем перерисовку перед включением анимации
        // eslint-disable-next-line no-unused-expressions
        carouselRef.current.offsetHeight;
        
        // Включаем анимацию снова
        carouselRef.current.style.transition = 'transform 0.5s ease';
      }
    }, 0);
    
    // Обновляем размеры при изменении окна
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [carousel, cards.length, slideWidth]);
  
  // Плавная прокрутка влево
  const handleScrollLeft = useCallback(() => {
    if (isAnimating || !carouselRef.current || slideWidth === 0) return;
    setIsAnimating(true);
    
    // Создаем новый индекс с защитой от выхода за пределы массива
    const newIndex = (currentIndex - 1 + cards.length) % cards.length;
    
    // Вычисляем новую позицию для плавной анимации
    const currentTransform = -(cards.length + currentIndex) * slideWidth;
    const newTransform = currentTransform + slideWidth;
    
    // Анимируем движение
    carouselRef.current.style.transition = 'transform 0.5s cubic-bezier(0.25, 1, 0.5, 1)';
    carouselRef.current.style.transform = `translateX(${newTransform}px)`;
    
    // После завершения анимации
    setTimeout(() => {
      setCurrentIndex(newIndex);
      
      // Если мы достигли начала клонированного набора, перепрыгиваем к оригинальному набору
      if (currentIndex === 0) {
        carouselRef.current.style.transition = 'none';
        carouselRef.current.style.transform = `translateX(-${(cards.length * 2 - 1) * slideWidth}px)`;
      }
      
      setTimeout(() => {
        setIsAnimating(false);
      }, 50);
    }, 500);
  }, [currentIndex, isAnimating, cards.length, slideWidth]);
  
  // Плавная прокрутка вправо
  const handleScrollRight = useCallback(() => {
    if (isAnimating || !carouselRef.current || slideWidth === 0) return;
    setIsAnimating(true);
    
    // Создаем новый индекс с защитой от выхода за пределы массива
    const newIndex = (currentIndex + 1) % cards.length;
    
    // Вычисляем новую позицию для плавной анимации
    const currentTransform = -(cards.length + currentIndex) * slideWidth;
    const newTransform = currentTransform - slideWidth;
    
    // Анимируем движение
    carouselRef.current.style.transition = 'transform 0.5s cubic-bezier(0.25, 1, 0.5, 1)';
    carouselRef.current.style.transform = `translateX(${newTransform}px)`;
    
    // После завершения анимации
    setTimeout(() => {
      setCurrentIndex(newIndex);
      
      // Если мы достигли конца клонированного набора, перепрыгиваем к оригинальному набору
      if (currentIndex === cards.length - 1) {
        carouselRef.current.style.transition = 'none';
        carouselRef.current.style.transform = `translateX(-${cards.length * slideWidth}px)`;
      }
      
      setTimeout(() => {
        setIsAnimating(false);
      }, 50);
    }, 500);
  }, [currentIndex, isAnimating, cards.length, slideWidth]);

  // Обработка свайпов на мобильных устройствах
  useEffect(() => {
    if (!carousel || !carouselRef.current) return;
    
    let touchStartX = 0;
    let touchEndX = 0;
    
    const handleTouchStart = (e) => {
      touchStartX = e.changedTouches[0].screenX;
    };
    
    const handleTouchEnd = (e) => {
      touchEndX = e.changedTouches[0].screenX;
      
      const swipeThreshold = 50;
      if (touchEndX - touchStartX > swipeThreshold) {
        // Свайп вправо - предыдущий слайд
        handleScrollLeft();
      } else if (touchStartX - touchEndX > swipeThreshold) {
        // Свайп влево - следующий слайд
        handleScrollRight();
      }
    };
    
    const carouselElement = carouselRef.current;
    carouselElement.addEventListener('touchstart', handleTouchStart, { passive: true });
    carouselElement.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    return () => {
      carouselElement.removeEventListener('touchstart', handleTouchStart);
      carouselElement.removeEventListener('touchend', handleTouchEnd);
    };
  }, [carousel, handleScrollLeft, handleScrollRight]);

  // Обработчики для drag-and-drop
  const handleDragStart = useCallback((e) => {
    if (!carousel || isAnimating) return;
    
    setIsDragging(true);
    setDragStartTime(Date.now());
    
    // Сохраняем начальную позицию курсора/касания
    const pageX = e.type.includes('mouse') ? e.pageX : e.touches[0].pageX;
    setStartX(pageX - carouselRef.current.offsetLeft);
    setScrollPosition(-(cards.length + currentIndex) * slideWidth);
    
    // Отключаем transition на время перетаскивания
    if (carouselRef.current) {
      carouselRef.current.style.transition = 'none';
    }
  }, [carousel, isAnimating, cards.length, currentIndex, slideWidth]);

  const handleDragMove = useCallback((e) => {
    if (!isDragging || !carousel || isAnimating) return;
    e.preventDefault();
    
    // Получаем текущую позицию курсора/касания
    const pageX = e.type.includes('mouse') ? e.pageX : e.touches[0].pageX;
    const x = pageX - carouselRef.current.offsetLeft;
    const dragDistance = x - startX;
    
    // Обновляем положение карусели
    if (carouselRef.current) {
      carouselRef.current.style.transform = `translateX(${scrollPosition + dragDistance}px)`;
    }
  }, [isDragging, carousel, isAnimating, startX, scrollPosition]);

  const handleDragEnd = useCallback((e) => {
    if (!isDragging || !carousel) return;
    setIsDragging(false);
    
    // Получаем конечную позицию
    const pageX = e.type.includes('mouse') ? e.pageX : e.changedTouches[0].pageX;
    const x = pageX - carouselRef.current.offsetLeft;
    const dragDistance = x - startX;
    
    // Вычисляем скорость свайпа
    const dragTime = Date.now() - dragStartTime;
    const dragSpeed = Math.abs(dragDistance) / dragTime;
    
    // Включаем обратно transition
    if (carouselRef.current) {
      carouselRef.current.style.transition = 'transform 0.5s cubic-bezier(0.25, 1, 0.5, 1)';
    }
    
    // Определяем направление прокрутки на основе расстояния и скорости
    const threshold = slideWidth * 0.2; // 20% от ширины слайда
    if (Math.abs(dragDistance) > threshold || dragSpeed > 0.5) {
      if (dragDistance > 0) {
        handleScrollLeft();
      } else {
        handleScrollRight();
      }
    } else {
      // Возвращаем к текущему слайду, если перетаскивание было недостаточным
      if (carouselRef.current) {
        carouselRef.current.style.transform = `translateX(${scrollPosition}px)`;
      }
    }
  }, [isDragging, carousel, startX, scrollPosition, dragStartTime, slideWidth, handleScrollLeft, handleScrollRight]);

  // Добавляем обработчики событий
  useEffect(() => {
    if (!carousel || !carouselRef.current) return;
    
    const element = carouselRef.current;
    
    // Мышь
    element.addEventListener('mousedown', handleDragStart);
    window.addEventListener('mousemove', handleDragMove);
    window.addEventListener('mouseup', handleDragEnd);
    
    // Касание
    element.addEventListener('touchstart', handleDragStart, { passive: true });
    element.addEventListener('touchmove', handleDragMove, { passive: false });
    element.addEventListener('touchend', handleDragEnd);
    
    return () => {
      // Удаляем обработчики при размонтировании
      element.removeEventListener('mousedown', handleDragStart);
      window.removeEventListener('mousemove', handleDragMove);
      window.removeEventListener('mouseup', handleDragEnd);
      
      element.removeEventListener('touchstart', handleDragStart);
      element.removeEventListener('touchmove', handleDragMove);
      element.removeEventListener('touchend', handleDragEnd);
    };
  }, [carousel, handleDragStart, handleDragMove, handleDragEnd]);

  return (
    <section className={styles.sectionCards}>
      <div 
        className={styles.sectionCardsContainer}
        style={{ maxWidth: maxCardsWidth }}
      >
        {/* Контейнер для карусели с кнопками */}
        <div className={`${carousel ? styles.carouselContainer : ''}`}>
          {carousel && (
            <button 
              className={`${styles.carouselButton} ${styles.prevButton}`} 
              onClick={handleScrollLeft}
              aria-label="Предыдущий слайд"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
          
          <div className={`${styles.carouselViewport} ${carousel ? styles.carouselActive : ''}`}>
            <div 
              ref={carouselRef}
              className={`${styles.cardsWrapper} ${carousel ? styles.carousel : ''} ${isDragging ? styles.dragging : ''}`}
              style={carousel ? { 
                transform: `translateX(-${(cards.length + currentIndex) * slideWidth}px)`,
                cursor: isDragging ? 'grabbing' : 'grab'
              } : {}}
            >
              {carousel ? 
                // Повторенные карточки для бесконечной карусели
                repeatedCards.map((card, index) => (
                  <div 
                    key={`carousel-${index}`} 
                    className={`${styles.card} ${styles.carouselCard}`} 
                    style={{ maxWidth: maxCardWidth }}
                  >
                    <img src={card.image} alt={card.title}/>
                    <h3 style={{ height: maxCardTitleHeight }}>{card.title}</h3>
                    {card.description && (
                      <p style={{ height: maxCardHeight }}>{card.description}</p>
                    )}
                    {card.price && (
                      <div className={styles.price}>
                        <span>{card.price} ₽</span>
                      </div>
                    )}
                  </div>
                ))
                :
                // Обычный режим без карусели
                cards.map((card, index) => (
                  <div 
                    key={index} 
                    className={styles.card} 
                    style={{ maxWidth: maxCardWidth }}
                  >
                    <img src={card.image} alt={card.title}/>
                    <h3 style={{ height: maxCardTitleHeight }}>{card.title}</h3>
                    {card.description && (
                      <p style={{ height: maxCardHeight }}>{card.description}</p>
                    )}
                    {card.price && (
                      <div className={styles.price}>
                        <span>{card.price} ₽</span>
                      </div>
                    )}
                  </div>
                ))
              }
            </div>
          </div>
          
          {carousel && (
            <button 
              className={`${styles.carouselButton} ${styles.nextButton}`}
              onClick={handleScrollRight}
              aria-label="Следующий слайд"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

Cards.propTypes = {
  cards: PropTypes.arrayOf(PropTypes.shape({
    image: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  })).isRequired,
  maxCardHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxCardsWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxCardWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  carousel: PropTypes.bool,
  itemsPerSlide: PropTypes.number
};

export default Cards; 